import React, { useEffect, useMemo, useState } from 'react';
import { API } from 'aws-amplify';
import { useTheme } from '@mui/material/styles';
import Game from '../../_types/Game';

type Props = {
  game: Game;
};

const getLeaderboardsQuery = /* GraphQL */ `
  query GetLeaderboards($request: AWSJSON!) {
    getLeaderboards(request: $request)
  }
`;

const parseFlex = (flexFields: any): any => {
  try {
    if (!flexFields) return {};
    return typeof flexFields === 'string' ? JSON.parse(flexFields) : flexFields;
  } catch (e) {
    return {};
  }
};

const IncentiveProgress: React.FC<Props> = ({ game }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const meId = (global as any)?.me?.id || (global as any)?.me?.userID || '';
  const scope = (game as any)?.scope || 'person';

  const mapping = useMemo(() => {
    const parsed = parseFlex((game as any).flexFields);
    return parsed?.incentive?.kpiToMissionID || {};
  }, [game]);

  useEffect(() => {
    const fetchProgress = async () => {
      try {
        setLoading(true);
        setError(null);

        const entries = Object.entries(mapping) as Array<[string, string]>; // [kpiId, missionId]
        if (entries.length === 0) {
          setLoading(false);
          return;
        }

        const results = await Promise.all(
          entries.map(async ([kpiId, missionId]) => {
            try {
              const response: any = await API.graphql({
                query: getLeaderboardsQuery,
                variables: {
                  request: JSON.stringify({ missionID: missionId }),
                },
              });
              const jsonRes = JSON.parse(
                response?.data?.getLeaderboards || '{}'
              );
              const mission = jsonRes?.Items?.[0];
              const users = mission?.users?.items || [];

              if (scope === 'person') {
                const my = users.find((u: any) => u?.user?.id === meId);
                const done = my?.doneActionCount ?? 0;
                return [kpiId, Number(done)] as [string, number];
              }

              // circle/team: aggregate doneActionCount
              const sum = users.reduce(
                (acc: number, u: any) => acc + Number(u?.doneActionCount ?? 0),
                0
              );
              return [kpiId, sum] as [string, number];
            } catch (e) {
              return [kpiId, 0] as [string, number];
            }
          })
        );

        console.debug('Incentive KPI results', results);
      } catch (e: any) {
        setError('Failed to load KPI progress');
      } finally {
        setLoading(false);
      }
    };

    fetchProgress();
  }, [mapping, meId, scope]);

  if (loading) {
    return (
      <div style={{ padding: 16, color: theme.custom?.text }}>
        Loading KPI progress...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 16, color: theme.custom?.text }}>{error}</div>
    );
  }

  return (
    <div style={{ padding: 16 }}>
      <div
        style={{
          marginBottom: 12,
          color: theme.custom?.text,
          fontWeight: 600,
          fontSize: 16,
        }}
      >
        All KPIs must be met to earn the reward
      </div>
    </div>
  );
};

export default IncentiveProgress;
